import React from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { Box, Paper, Typography } from '@mui/material';

import { SMS_SENDER_PHONE } from './patient-communication/constants';

interface MessagePreviewProps {
  deliveryMethod: 'sms' | 'email' | 'both';
  patientName?: string;
  providerName?: string;
  formLink?: string;
  documentLink?: string;
  patientPhone?: string;
  customMessage?: string;
  contentType?: 'form' | 'document' | 'lab_order';
}

export const MessagePreview: React.FC<MessagePreviewProps> = ({
  deliveryMethod,
  patientName,
  providerName = 'Your Provider',
  formLink = 'https://jotpsy.ch/f/7f6ec2aa',
  documentLink,
  patientPhone,
  customMessage,
  contentType = 'form',
}) => {
  const { singularCap: subjectLanguageCap } = useSubjectLanguageVariants();
  const displayPatientName = patientName || subjectLanguageCap;
  const link =
    contentType === 'document' || contentType === 'lab_order'
      ? documentLink || '[Document Link]'
      : formLink;
  const renderSmsPreview = () => {
    // Build the SMS message based on content type
    const getSmsMessage = () => {
      if (contentType === 'lab_order') {
        return (
          <>
            Hi {displayPatientName}, a lab requisition order from{' '}
            {providerName || '[Provider Name]'} is ready.
            <br />
            <br />
            View it here:{' '}
            <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              {link}
            </Box>
            {customMessage && (
              <>
                <br />
                <br />
                {customMessage}
              </>
            )}
            <br />
            <br />
            Reply STOP to unsubscribe
          </>
        );
      } else if (contentType === 'document') {
        return (
          <>
            {providerName || '[Provider Name]'} has sent you a document.
            <br />
            <br />
            View it here:{' '}
            <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              {link}
            </Box>
            {customMessage && (
              <>
                <br />
                <br />
                {customMessage}
              </>
            )}
            <br />
            <br />
            Reply STOP to unsubscribe
          </>
        );
      } else {
        return (
          <>
            Hello! {providerName} has sent you a new form to complete. Please tap the link below to
            fill it out:{' '}
            <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              {link}
            </Box>
            {customMessage && (
              <>
                <br />
                <br />
                {customMessage}
              </>
            )}
          </>
        );
      }
    };

    return (
      <Box>
        <Typography variant="body2" component="div">
          {getSmsMessage()}
        </Typography>
        <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
          {subjectLanguageCap} will receive a text message from {SMS_SENDER_PHONE}
        </Typography>
      </Box>
    );
  };

  const renderEmailPreview = () => {
    const subject =
      contentType === 'lab_order'
        ? `Lab Requisition Order from ${providerName || '[Provider Name]'}`
        : contentType === 'document'
          ? `Document Ready from ${providerName || '[Provider Name]'}`
          : `New Form from ${providerName}`;

    const getEmailBody = () => {
      if (contentType === 'lab_order') {
        return (
          <>
            Hi {displayPatientName},
            <br />
            <br />
            Your lab requisition order from {providerName || '[Provider Name]'} is now available.
            <br />
            <br />
            <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              {link}
            </Box>
            {customMessage && (
              <>
                <br />
                <br />
                {customMessage}
              </>
            )}
            <br />
            <br />
            This email was sent on behalf of {providerName || '[Provider Name]'}. If you have
            questions, please contact their office directly.
          </>
        );
      } else if (contentType === 'document') {
        return (
          <>
            {providerName || '[Provider Name]'} has sent you a document.
            <br />
            <br />
            View it here:{' '}
            <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              {link}
            </Box>
            {customMessage && (
              <>
                <br />
                <br />
                {customMessage}
              </>
            )}
            <br />
            <br />
            This email was sent on behalf of {providerName || '[Provider Name]'}. If you have
            questions, please contact their office directly.
          </>
        );
      } else {
        return (
          <>
            Hello {displayPatientName},
            <br />
            <br />
            {providerName} has sent you a new form to complete.
            <br />
            <br />
            Please click the link below to access and fill out your form:
            <br />
            <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              {link}
            </Box>
            {customMessage && (
              <>
                <br />
                <br />
                {customMessage}
              </>
            )}
            <br />
            <br />
            If you have any questions, please contact your healthcare provider&apos;s office.
            <br />
            <br />
            Thank you,
            <br />
            Your Healthcare Team
          </>
        );
      }
    };

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Subject: {subject}
        </Typography>
        <Typography variant="body2" component="div">
          {getEmailBody()}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        py: 2.5,
        px: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {deliveryMethod === 'both' ? (
        <>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            SMS Message
          </Typography>
          {renderSmsPreview()}

          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Email Message
            </Typography>
            {renderEmailPreview()}
          </Box>
        </>
      ) : deliveryMethod === 'sms' ? (
        renderSmsPreview()
      ) : (
        renderEmailPreview()
      )}
    </Paper>
  );
};

export default MessagePreview;
